"use client";

import * as Headless from "@headlessui/react";
import clsx from "clsx";
import { LayoutGroup, motion } from "framer-motion";
import React, { forwardRef, useId } from "react";
import { TouchTarget } from "./button";
import { Link } from "./link";

export function Navbar({ className, ...props }: React.ComponentPropsWithoutRef<"nav">) {
  return <nav {...props} className={clsx(className, "flex flex-1 items-center gap-4 py-2.5")} />;
}

export function NavbarDivider({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return <div aria-hidden="true" {...props} className={clsx(className, "h-6 w-px bg-gray-200 dark:bg-avemio-dark-border")} />;
}

export function NavbarSection({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div {...props} className={clsx(className, "flex items-center gap-3")} />
    </LayoutGroup>
  );
}

export function NavbarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return <div aria-hidden="true" {...props} className={clsx(className, "-ml-4 flex-1")} />;
}

export const NavbarItem = forwardRef(function NavbarItem(
  {
    current,
    className,
    children,
    ...props
  }: { current?: boolean; className?: string; children: React.ReactNode } & (
    | Omit<Headless.ButtonProps, "as" | "className">
    | Omit<React.ComponentPropsWithoutRef<typeof Link>, "className">
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
  const classes = clsx(
    // Base
    "relative flex min-w-0 items-center gap-2.5 rounded-md p-2 text-left text-sm font-medium text-gray-900",
    // Transitions
    "transition-all duration-200 ease-out",
    // Leading icon/icon-only
    "*:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:fill-gray-600 *:data-[slot=icon]:transition-all *:data-[slot=icon]:duration-200",
    // Trailing icon (down chevron or similar)
    "*:not-nth-2:last:data-[slot=icon]:ml-auto *:not-nth-2:last:data-[slot=icon]:size-4",
    // Avatar
    "*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-9 *:data-[slot=avatar]:[--avatar-radius:var(--radius)] *:data-[slot=avatar]:[--ring-opacity:10%] *:data-[slot=avatar]:transition-transform *:data-[slot=avatar]:duration-200",
    // Beautiful Hover
    "data-hover:bg-gray-50 data-hover:*:data-[slot=icon]:fill-gray-900 data-hover:*:data-[slot=avatar]:scale-105",
    // Active
    "data-active:bg-gray-100 data-active:*:data-[slot=icon]:fill-gray-900",
    // Dark mode
    "dark:text-avemio-dark-text-primary dark:*:data-[slot=icon]:fill-avemio-dark-text-secondary",
    "dark:data-hover:bg-avemio-dark-surface-elevated dark:data-hover:*:data-[slot=icon]:fill-avemio-dark-text-primary",
    "dark:data-active:bg-avemio-dark-surface-elevated dark:data-active:*:data-[slot=icon]:fill-avemio-dark-text-primary",
  );

  return (
    <span className={clsx(className, "relative")}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="absolute inset-x-2 -bottom-2.5 h-0.5 rounded-full bg-avemio-primary dark:bg-avemio-primary"
        />
      )}
      {"href" in props && typeof props.href === "string" ? (
        (() => {
          const { to, ...linkProps } = props as any;
          return (
            <Link
              {...linkProps}
              href={props.href}
              className={classes}
              data-current={current ? "true" : undefined}
              ref={ref as React.ForwardedRef<HTMLAnchorElement>}
            >
              <TouchTarget>{children}</TouchTarget>
            </Link>
          );
        })()
      ) : (
        (() => {
          const { to, href, download, hrefLang, ...buttonProps } = props as any;
          return (
            <Headless.Button
              {...buttonProps}
              className={clsx("cursor-default", classes)}
              data-current={current ? "true" : undefined}
              ref={ref}
            >
              <TouchTarget>{children}</TouchTarget>
            </Headless.Button>
          );
        })()
      )}
    </span>
  );
});

export function NavbarLabel({ className, ...props }: React.ComponentPropsWithoutRef<"span">) {
  return <span {...props} className={clsx(className, "truncate")} />;
}
